import * as XLSX from "xlsx";

// Converts a value (string, number, object, or array of them) into readable
// text for a spreadsheet cell. Arrays such as skills / preferred cities are
// joined; objects are reduced to their most likely label field.
const toText = (value) => {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) {
    return value.map(toText).filter((v) => v !== "").join(", ");
  }
  if (typeof value === "object") {
    return toText(
      value.title ??
        value.name ??
        value.skill ??
        value.skill_name ??
        value.city ??
        value.city_name ??
        value.label ??
        value.value ??
        ""
    );
  }
  return String(value);
};

// Reads the first non-empty value for a field, checking both the candidate
// record and a nested `basic_details` object (the admin API can return either).
const pick = (candidate, keys) => {
  const details = candidate?.basic_details ?? {};
  for (const key of keys) {
    const top = candidate?.[key];
    if (top !== undefined && top !== null && top !== "") return top;
    const nested = details?.[key];
    if (nested !== undefined && nested !== null && nested !== "") return nested;
  }
  return "";
};

const candidateName = (candidate) => {
  const direct = pick(candidate, ["name", "candidate_name", "full_name"]);
  if (direct) return toText(direct);
  const joined = [candidate?.first_name, candidate?.last_name].filter(Boolean).join(" ");
  return joined || "";
};

// Maps candidate records to the columns requested for the Excel export.
// Kept pure (no XLSX / DOM) so it can be unit tested.
export const buildCandidateRows = (candidates = []) =>
  (Array.isArray(candidates) ? candidates : []).map((candidate) => ({
    "Candidate Name": candidateName(candidate),
    Email: toText(pick(candidate, ["email", "email_id"])),
    Phone: toText(pick(candidate, ["phone", "mobile", "phone_number", "contact"])),
    Gender: toText(pick(candidate, ["gender"])),
    "Date Of Birth": toText(pick(candidate, ["dob", "date_of_birth"])),
    "Experience(Year)": toText(pick(candidate, ["experience_years", "experience_year", "exp_year"])),
    "Experience(Month)": toText(pick(candidate, ["experience_months", "experience_month", "exp_month"])),
    "Work Status": toText(pick(candidate, ["work_status"])),
    "Current City": toText(pick(candidate, ["current_city", "city", "current_location", "present_city"])),
    Skill: toText(pick(candidate, ["skills", "skill", "it_skills", "candidate_skills"])),
    "Job Title": toText(pick(candidate, ["job_title", "designation", "title"])),
    "Preferred Cities": toText(
      pick(candidate, ["preferred_location", "preferred_cities", "preferred_city", "preferred_locations"])
    ),
  }));

// Builds an XLSX workbook (in-memory) from candidate records.
export const buildCandidateWorkbook = (candidates = []) => {
  const rows = buildCandidateRows(candidates);
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
  return workbook;
};

// Exports candidate records to an .xlsx file and triggers a browser download.
export const exportCandidatesToExcel = (candidates = [], filename) => {
  const workbook = buildCandidateWorkbook(candidates);
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, filename || `candidates-${stamp}.xlsx`);
};
